export default {
  name: "Job Opportunity and Wage Information",
  prefix: "E",
  pageInfo: {
    text: `<b><u>Note:</u></b> The job opportunity and wage information data will be directly imported from the Form ETA-9141, 
    <i>Application for Prevailing Wage Determination</i> associated with the PWD tracking number. The PWD captures the employer 
    and agent information, as well as the specific job requirements, special skills, licenses, certifications, etc., 
    required for the job opportunity. The PWD also lists the specific worksite(s) where the work will be performed. 
    OFLC’s electronic filing system will use this same information to confirm the area of intended employment and 
    the job duties and requirements for the job opportunity listed on the Form ETA-9089. By using the PWD tracking 
    number to link and pre-populate certain areas of the Form ETA-9089, OFLC enhances the application submission and 
    review process by reducing the amount of time and potential data entry errors associated with manually entering 
    data into fields that historically have not been linked, which produces more accurate applications that can 
    be reviewed more quickly. This enhancement also limits any modification between the approval of the PWD and 
    the filing of the Form ETA-9089, thus reducing potential clerical errors that would result in a request for 
    information, Audit Notification, or Denial. If the PWD includes two wages, the wage in Section G.4 of
    the PWD is based on the minimum requirements, and the wage in Section G.5 of the PWD is based on the 
    alternative requirements. The higher of the two wages as the determined prevailing wage must be used 
    when completing the Form ETA-9089, <i>Application for Permanent Employment Certification</i>. The offered wage 
    must be equal to or greater than the higher wage of the two sets of requirements. If the employer submits 
    a Form ETA-9089 with only one set of requirements (whether the minimum or alternative requirements), the 
    offered wage in Section E.3 must be at least the prevailing wage associated with that set of requirements on the PWD.
    <br/><br/>
    In the event that an employer must file the Form ETA-9089 by mail, OFLC’s electronic filing system will 
    link the approved PWD by tracking number. <b><i>The PWD sections that will be linked to the Form ETA-9089 are the 
    Employer Information, Attorney or Agent Information, the Wage Source, and the Job Offer Information</i></b>.`,
    coloring: "grey",
  },
  sections: [
    {
      fields: [
        // {
        //   code: "E.1",
        //   label: `Enter the valid Prevailing Wage Determination (PWD) case number issued by the Department of Labor to identify the job opportunity and prevailing wage(s) covered by this application. <span style="font-size:13.62px">*</span>`,
        //   widget: "label",
        // },
        {
          code: "E.1",
          machine_name: "jobOppPWDNumber",
          label: `Enter the valid Prevailing Wage Determination (PWD) case number issued by the Department of Labor to identify the job opportunity and prevailing wage(s) covered by this application. <span style="font-size:13.62px">*</span>`,
          widget: "text",
          disabled: true,
          tooltip:
            "Enter the 14-digit PWD number assigned by the Foreign Labor Application Gateway (FLAG) System or the National Prevailing Wage Center for the job opportunity listed on the application.  Example: P-100-xxxxx-xxxxxx.",
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
          ],
        },
        {
          isGroup: {
            code: "E.2",
            machine_name: "jobOppPWDAttached",
            required: true,
            label: `If a valid PWD has <u>not</u> been obtained due to the employer being required, by notice from a Certifying Officer, to currently undergo supervised recruitment in accordance with 20 CFR 656.21, indicate whether a completed Form ETA-9141 is attached to this application.`,
            tooltip:
              "If the employer has been notified that it must undergo supervised recruitment for the job opportunity covered by this application, as set forth in 20 CFR 655.21, <u>and</u> a valid PWD has <u>not</u> been obtained, indicate whether a completed Form ETA-9141 is attached to this application by marking “Yes” or “No”.  If the employer has not been notified that it must undergo supervised recruitment for the job opportunity covered by this application, mark “N/A”.",
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "<span style='color:#707275'>Yes</span>",
                value: "Yes",
                disabled: true,
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "<span style='color:#707275'>No</span>", //css placeholder until color fixed globally
                value: "No",
                disabled: true,
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "<span style='color:#707275'>N/A</span>",
                value: "N/A",
                disabled: true,
                validate: ["required"],
              },
            ],
          },
        },
        {
          machine_name: "pwdWageRate",
          label: "Your 9141 Prevailing Wage",
          widget: "text",
          disabled: true,
          reviewHidden: true,
        },
        {
          machine_name: "pwdUnitOfPay",
          label: "Your 9141 Prevailing Wage Rate",
          widget: "text",
          disabled: true,
          reviewHidden: true,
        },
        {
          isGroup: {
            code: "E.3",
            label: "Offered Wage",
            showIndividualFieldError: true,
            required: true,
            tooltip:
              "Enter the rate of pay to be paid to the worker.  If the wage offer is expressed as a range, enter the bottom of the wage range to be paid on the “From:” line and enter the top of the wage range on the “To:” line.",
            fields: [
              {
                machine_name: "jobOppWageFrom",
                widget: "wage",
                label: "From",
                fromFields: ["pwdWageRate"],
                validate: [
                  "required",
                  {
                    fieldComparison: "jobOppWageFrom",
                    message: "From cannot be greater than To",
                    needsFieldValues: true,
                    extraData: {
                      operator: "<=",
                      valueOf: "jobOppWageTo",
                    },
                  },
                  {
                    prevailingWageCheck: "jobOppWageFrom",
                    message:
                      "If the PWD includes two wages, the wage rate must be equal to or higher than the highest of the prevailing wages assigned.",
                    needsFieldValues: true,
                    extraData: {
                      prevailing_wage: "pwdWageRate",
                      prevailing_wage_per: "pwdUnitOfPay",
                      wage_per: "jobOppWagePer",
                    },
                  },
                ],
                maxLength: 9,
                placeholder: "$000.000.00",
              },
              {
                machine_name: "jobOppWageTo",
                widget: "wage",
                label: "To",
                maxLength: 9,
                placeholder: "$000.000.00",
                validate: [
                  {
                    fieldComparison: "jobOppWageTo",
                    message: "To cannot be less than From",
                    needsFieldValues: true,
                    extraData: {
                      operator: ">=",
                      valueOf: "jobOppWageFrom",
                    },
                  },
                  {
                    prevailingWageCheck: "jobOppWageTo",
                    message:
                      "If the PWD includes two wages, the wage rate must be equal to or higher than the highest of the prevailing wages assigned.",
                    needsFieldValues: true,
                    extraData: {
                      prevailing_wage: "pwdWageRate",
                      prevailing_wage_per: "pwdUnitOfPay",
                      wage_per: "jobOppWagePer",
                    },
                  },
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.4",
            machine_name: "jobOppWagePer",
            label: "Per (Choose only one)",
            required: true,
            tooltip: `Indicate whether the rate of pay is per hour, week, bi-weekly, month, or year by selecting the corresponding box.  Make only one selection.`,
            clearBtn: {
              clearSchema: ["jobOppWagePer"],
            },
            fields: [
              {
                widget: "radio",
                choice: "Hour",
                value: "Hour",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Week",
                value: "Week",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Bi-Weekly",
                value: "Bi-Weekly",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Month",
                value: "Month",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Year",
                value: "Year",
                validate: ["required"],
              },
            ],
          },
        },
        {
          code: "E.5",
          machine_name: "jobOppWageConditions",
          label:
            "Additional conditions about the offered wage (Enter up to 500 characters) §",
          tooltip:
            "Briefly describe any conditions about the wage rate to be paid.  For example, <b>“the wage rate does not account for future increases due to annual cost of living increases or allowances.”</b>  Please also describe here any bonuses, fringe benefits, subsidized housing or meals, or any other benefits associated with this job opportunity, as applicable.",
          widget: "text_area",
          validate: [
            {
              minLengthWithNA: 5,
              message: "E.5 must contain at least 5 characters",
            },
          ],
          inputLimit: 500,
          width: 8,
          rows: 8,
        },
      ],
    },
  ],
};
